import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { ClassWebsiteUnitsToggle } from "@shared/models/analytics/class-website"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "../../action-doc-builder"
import { get, partial } from "lodash-es"
import type { Narrative } from "../create-narrative"
import defaultClassWebsiteActivity from "./default-class-website-activity"
import defaultClassWebsiteModels from "./default-class-website-models"

interface Payload {
  planbookId: string
  websiteId: string
  isPublic: boolean
}

export default function toggleUnitsOnClassWebsiteAction(payload: Payload): PreparedAction {
  return {
    name: "TOGGLE_UNITS_ON_CLASS_WEBSITE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload) {
  let patch = Patch.create("class-website", payload.websiteId).set(
    "attributes.unitsArePublic",
    payload.isPublic
  ).patch

  return [patch]
}

function undoPatches(payload: Payload) {
  let patch = Patch.create("class-website", payload.websiteId).set(
    "attributes.unitsArePublic",
    !payload.isPublic
  ).patch
  return [patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultClassWebsiteModels(payload, findFn, userId)
  let websiteTitle = get(models, "classWebsite.attributes.title")
  let titleForAnalytics = payload.isPublic
    ? "Class Website - Units Toggled On"
    : "Class Website - Units Toggled Off"
  let newEvent: ClassWebsiteUnitsToggle = {
    ...createBlankEvent(),
    root_id: models.planbook.id,
    root_type: "planbook",
    date_plan_scheduled_for: null,

    primary_verb: "customize",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),

    primary_object_type: "class_website",
    primary_object_id: payload.websiteId,
    primary_object_specifier: null,
    primary_object_id_and_ancestor_ids: [payload.planbookId, payload.websiteId],

    adverbial_verb: "toggling",

    adverbial_object_type: "unit",
    adverbial_object_id: null,
    adverbial_object_specifier: payload.isPublic ? "on" : "off",
    adverbial_object_id_and_ancestor_ids: [payload.planbookId, payload.websiteId],
  }

  return {
    title: "Toggled Units",
    titleForAnalytics: titleForAnalytics,
    description: `Units have been toggled on the ${websiteTitle} class website.`,
    activity: defaultClassWebsiteActivity(payload, models),
    context: {},
    newAnalyticsEvents: [newEvent],
  }
}
